import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import validate from 'validate.js';
import * as actions from 'redux/actions/sessionActions';
import alertify from 'alertifyjs';
import 'alertifyjs/build/css/alertify.css';
import { useLocation } from 'react-router-dom';
import { Translate, I18n } from 'react-redux-i18n';
import 'assets/css/signin.css';
import { FaEyeSlash, FaEye } from 'react-icons/fa';
import { mapPasswordApiError, isPasswordValid } from 'utils/passwordPolicy';

const schema = {
    pwd_new: {
        presence: { allowEmpty: false, message: 'is required' },
        passwordPolicy: true,
        length: {
            maximum: 128,
        },
    },
    pwd_new1: {
        presence: { allowEmpty: false, message: 'is required' },
        length: {
            maximum: 128,
        },
    },
};

const Reset = (props) => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const { history } = props;

    const [formState, setFormState] = useState({
        isValid: false,
        showPassword: false,
        values: {
            token: params.get('t'),
        },
        touched: {},
        errors: {},
    });

    const translateError = (message) => {
        if (!message) {
            return null;
        }
        const key = message.startsWith('^') ? message.slice(1) : message;
        const translated = I18n.t(key);
        return translated !== key ? translated : message;
    };

    useEffect(() => {
        props.Logout();
    }, []);

    useEffect(() => {
        if (props.error) {
            const message = mapPasswordApiError(props.error);
            const translated = I18n.t(message);
            alertify.error(translated !== message ? translated : props.error);
        }
    }, [props.error]);

    useEffect(() => {
        if (props.success) {
            alertify.success(props.success);
            history.push('/login');
        }
    }, [props.success, history]);

    useEffect(() => {
        const errors = validate(formState.values, schema) || {};

        if (
            formState.touched.pwd_new1
            && formState.values.pwd_new1
            && formState.values.pwd_new !== formState.values.pwd_new1
        ) {
            errors.pwd_new1 = ['^password_mismatch'];
        }

        const passwordsMatch = formState.values.pwd_new === formState.values.pwd_new1;
        const policyValid = isPasswordValid(formState.values.pwd_new);
        const hasRequiredFields = Boolean(
            formState.values.pwd_new
            && formState.values.pwd_new1
            && formState.values.token
        );

        setFormState((current) => ({
            ...current,
            isValid: Object.keys(errors).length === 0
                && policyValid
                && passwordsMatch
                && hasRequiredFields,
            errors,
        }));
    }, [formState.values, formState.touched]);

    const handleChange = (event) => {
        event.persist();

        setFormState((current) => ({
            ...current,
            values: {
                ...current.values,
                [event.target.name]: event.target.value,
            },
            touched: {
                ...current.touched,
                [event.target.name]: true,
            },
        }));
    };

    const handleActivate = async (event) => {
        event.preventDefault();

        if (formState.values.pwd_new !== formState.values.pwd_new1) {
            alertify.error(I18n.t('password_mismatch'));
            return;
        }

        await props.Activate(formState.values);
        setFormState((current) => ({
            ...current,
            isValid: false,
            values: {
                token: current.values.token,
            },
            touched: {},
            errors: {},
        }));
    };

    const hasError = (field) =>
        Boolean(formState.touched[field] && formState.errors[field]);

    const togglePasswordVisibility = () => {
        setFormState((current) => ({
            ...current,
            showPassword: !current.showPassword,
        }));
    };

    return (
        <div className="sign-in-page">
            <div className="signin-card">
                <div className="sign-in-content">
                    <div className="signin-header">
                        <div>
                            <h2 className="signin-title">
                                <Translate value="reset_password" />
                            </h2>
                            <span className="signin-subtitle">
                                <Translate value="update_password" />
                            </span>
                        </div>
                    </div>

                    <form className="signin-form" onSubmit={handleActivate}>
                        <div className="password-row">
                            <input
                                className={`input ${hasError('pwd_new') ? 'error' : ''}`}
                                name="pwd_new"
                                id="pwd_new"
                                type={formState.showPassword ? 'text' : 'password'}
                                onChange={handleChange}
                                value={formState.values.pwd_new || ''}
                                placeholder={I18n.t('password')}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="pwd-toggle"
                                onClick={togglePasswordVisibility}
                                aria-label="Toggle password visibility"
                            >
                                {formState.showPassword ? <FaEye /> : <FaEyeSlash />}
                            </button>
                        </div>
                        {hasError('pwd_new') && (
                            <span className="field-error">
                                {translateError(formState.errors.pwd_new[0])}
                            </span>
                        )}
                        <p className="password-hint">
                            <Translate value="password_policy_requirements" />
                        </p>

                        <div className="password-row">
                            <input
                                className={`input ${hasError('pwd_new1') ? 'error' : ''}`}
                                name="pwd_new1"
                                id="pwd_new1"
                                type={formState.showPassword ? 'text' : 'password'}
                                onChange={handleChange}
                                value={formState.values.pwd_new1 || ''}
                                placeholder={I18n.t('password_again')}
                                autoComplete="new-password"
                            />
                        </div>
                        {hasError('pwd_new1') && (
                            <span className="field-error">
                                {translateError(formState.errors.pwd_new1[0])}
                            </span>
                        )}

                        <button
                            type="submit"
                            className="btn btn-primary btn-block mt-2"
                            disabled={!formState.isValid}
                        >
                            <Translate value="update" />
                        </button>

                        <p className="signin-sugestion mt-3">
                            <Link className="linked-primary" to="/login">
                                <Translate value="back_to_login" />
                            </Link>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

Reset.propTypes = {
    history: PropTypes.object,
};

function mapStateToProps(reducer) {
    return {
        error: reducer.session.error,
        success: reducer.session.success,
    };
}

const mapDispatchToProps = (dispatch) => ({
    Activate: (params) =>
        dispatch(actions.activate({ params })),
    Logout: () =>
        dispatch(actions.logout()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Reset);
